/**
 * profilePhoto.ts — the clinician avatar-photo store (UX Wave 8 §1).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 8 (clinician
 * profile rebuild, 2026-07-21).
 *
 * WHY: the profile photo renders in TWO places — the "Your profile" panel and
 * the top account menu's avatar thumbnail (ui/chrome.tsx's ProfileMenu). The
 * two must agree the moment a new photo is saved, without prop-drilling
 * through the app shell or refetching on every header render. This is a tiny
 * module-level store (useSyncExternalStore-shaped: get/set/subscribe) holding
 * the CURRENT signed photo URL:
 *
 *   - `undefined`  → not known yet (the menu lazily fetches the profile once);
 *   - `null`       → known to have no photo (or a 404/no-record caller);
 *   - `string`     → the short-lived signed READ URL.
 *
 * The profile panel seeds it on load and updates it on save; the menu only
 * fetches when the value is still `undefined`. The URL is a ~15-minute signed
 * read — if it expires mid-session the <img> onError clears it back to the
 * initial-letter fallback (never a broken-image glyph). Session-scoped memory
 * only — deliberately NEVER localStorage (house rule: no client persistence).
 */

type PhotoUrl = string | null | undefined;

let current: PhotoUrl;
const listeners = new Set<() => void>();

/** Current value (undefined = unknown, null = no photo, string = signed URL). */
export function getProfilePhotoUrl(): PhotoUrl {
  return current;
}

/** Publish a new value and notify subscribers (profile load/save, menu fetch). */
export function setProfilePhotoUrl(url: PhotoUrl): void {
  if (current === url) return;
  current = url;
  for (const listener of listeners) listener();
}

/** useSyncExternalStore subscribe contract. */
export function subscribeProfilePhoto(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test/logout hygiene — forget the cached URL (back to "unknown"). */
export function resetProfilePhoto(): void {
  current = undefined;
}
